import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { Box, Typography, Link as MLink } from "@mui/material";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../theme/tokens";

export default function Markdown({ children }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
sx={{
    "& p": { mb: 1.5, lineHeight: 1.7 },

    "& h1": {
      mt: 3,
      mb: 1.5,
      fontSize: "1.5rem",
      fontWeight: 800,
      color: colors.scalpelTeal[400],
      lineHeight: 1.25,
    },

    "& h2": {
      mt: 3,
      mb: 1.25,
      fontSize: "1.2rem",
      fontWeight: 700,
      color: colors.text.primary,
      lineHeight: 1.3,
    },

    "& h3": {
      mt: 2.5,
      mb: 1,
      fontSize: "1rem",
      fontWeight: 700,
      color: colors.houndGold[400],
      lineHeight: 1.3,
    },

    "& ul, & ol": {
      pl: 3,
      mb: 1.5,
    },

    "& li": {
      mb: 0.75,
      lineHeight: 1.6,
    },

    "& hr": {
      border: 0,
      borderTop: `1px solid ${colors.houndGold[500]}`,
      opacity: 0.6,
      my: 2.5,
    },

    "& blockquote": {
      borderLeft: `4px solid ${colors.scalpelTeal[500]}`,
      pl: 2,
      py: 0.5,
      my: 2,
      color: colors.text.secondary,
      backgroundColor: "rgba(255,255,255,0.03)",
    },

    "& table": {
      width: "100%",
      borderCollapse: "collapse",
      mb: 2,
      overflowX: "auto",
      display: "block",
    },

    "& th": {
      border: `1px solid ${colors.scalpelTeal[800]}`,
      p: 1,
      fontWeight: 700,
      backgroundColor: "rgba(255,255,255,0.04)",
    },

    "& td": {
      border: `1px solid ${colors.scalpelTeal[800]}`,
      p: 1,
      verticalAlign: "top",
    },

    "& pre": {
      borderRadius: 1,
      overflow: "auto",
      my: 2,
    },
      "& li.task-list-item": {
      listStyle: "none",
      marginLeft: "-1.5rem",
    },

    "& code": {
      fontSize: "0.9em",
    },

    wordBreak: "break-word",
  }}
    >
      <ReactMarkdown
        // GFM + sanitize for safety
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          h1: ({ node, ...props }) => (
            <Typography variant="h1" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <Typography variant="h2" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <Typography variant="h3" {...props} />
          ),
          a: ({ node, ...props }) => (
            <MLink target="_blank" rel="noopener noreferrer" {...props} />
          ),
          input: ({ checked, ...props }) => {
              if (props.type === "checkbox") {
                return (
                  <Box
                    component="input"
                    type="checkbox"
                    checked={checked}
                    readOnly
                    sx={{
                      accentColor: colors.scalpelTeal[400],
                      width: 16,
                      height: 16,
                      mr: 1,
                    }}
                  />
                );
              }

              return <input {...props} />;
            },
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            if (!inline && match) {
              return (
                <SyntaxHighlighter
                  style={oneDark}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              );
            }
            return (
              <code
                style={{
                  background: "rgba(255,255,255,0.08)",
                  padding: "0 4px",
                  borderRadius: 4,
                }}
                {...props}
              >
                {children}
              </code>
            );
          },
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </Box>
  );
}
